// src/pages/dashboards/user/UserExpenses.jsx
// Expense management: filter by tax year / category / claimed + search,
// edit (category, amount, date, description, notes), toggle claimed, delete.

import React, { useEffect, useState, useCallback } from "react";
import {
  listExpenses, listCategories, updateExpense, toggleExpenseClaimed, deleteExpense, downloadTaxPack,
  listPractitioners, unlinkPractitioner, redeemCode, listReleases, releaseTaxPack, revokeRelease,
} from "../../../api/user";
import { Card, Input, Select, Button, Badge, Loading, EmptyState, Modal, Field } from "../../../components/ui";
import { Download, Users } from "../../../components/icons";
import ReceiptPanel from "../../../components/ReceiptPanel";
import { useToast } from "../../../context/ToastContext";
import { money, date } from "../../../lib/format";

const now = new Date();
const TAX_YEARS = [now.getFullYear() + 1, now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

export default function UserExpenses() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [cats, setCats] = useState([]);
  const [err, setErr] = useState("");

  const [taxYear, setTaxYear] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [claimed, setClaimed] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [packOpen, setPackOpen] = useState(false);
  const [pracOpen, setPracOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [practitioners, setPractitioners] = useState([]);
  const [releases, setReleases] = useState([]);

  const loadPractitioners = () =>
    listPractitioners().then(setPractitioners).catch(() => setPractitioners([]));
  const loadReleases = () =>
    listReleases().then(setReleases).catch(() => setReleases([]));

  const load = useCallback(async () => {
    setErr("");
    try {
      const params = {};
      if (taxYear) params.tax_year = parseInt(taxYear, 10);
      if (categoryId) params.category_id = categoryId;
      if (claimed !== "") params.is_claimed = claimed === "yes";
      if (q.trim()) params.q = q.trim();
      setRows(await listExpenses(params));
    } catch (e) { setErr(e.message); }
  }, [taxYear, categoryId, claimed, q]);

  useEffect(() => { listCategories().then(setCats).catch(() => {}); loadPractitioners(); loadReleases(); }, []);
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0); // debounce search
    return () => clearTimeout(t);
  }, [load, q]);

  const onToggle = async (row) => {
    try {
      await toggleExpenseClaimed(row.id);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const onDelete = async (row) => {
    if (!window.confirm("Archive this expense? It won't count toward deductions.")) return;
    try {
      await deleteExpense(row.id);
      toast.success("Expense archived");
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">Tax deductions</h1>
        <div className="flex items-center gap-2">
          <Button variant={practitioners.length ? "subtle" : "ghost"} onClick={() => setPracOpen(true)} className="inline-flex items-center gap-1.5">
            <Users size={16} />
            {practitioners.length
              ? <><span className="hidden sm:inline">Practitioner</span><Badge tone="green" className="ml-1">linked</Badge></>
              : <span className="hidden sm:inline">Link practitioner</span>
            }
          </Button>
          {practitioners.length > 0 && (
            <Button variant="subtle" onClick={() => setSubmitOpen(true)} className="inline-flex items-center gap-1.5">
              <Download size={16} className="rotate-180" /> <span className="hidden sm:inline">Submit to practitioner</span>
            </Button>
          )}
          <Button onClick={() => setPackOpen(true)} className="inline-flex items-center gap-1.5">
            <Download size={16} /> <span className="hidden sm:inline">Tax pack</span>
          </Button>
        </div>
      </div>

      {/* filters */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-5">
        <Input placeholder="Search description / notes" value={q} onChange={(e) => setQ(e.target.value)} className="col-span-2 sm:w-64" />
        <Select value={taxYear} onChange={(e) => setTaxYear(e.target.value)} className="w-full sm:w-auto">
          <option value="">All tax years</option>
          {TAX_YEARS.map((y) => <option key={y} value={y}>TY {y}</option>)}
        </Select>
        <Select value={claimed} onChange={(e) => setClaimed(e.target.value)} className="w-full sm:w-auto">
          <option value="">Claimed &amp; not</option>
          <option value="yes">Claimed only</option>
          <option value="no">Not claimed</option>
        </Select>
        <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-full sm:w-auto col-span-2 sm:col-span-1">
          <option value="">All categories</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
      </div>

      {err && <p className="text-sm text-red-600 mb-4">{err}</p>}

      <Card className="overflow-hidden">
        {!rows ? <Loading label="Loading expenses" /> : rows.length === 0 ? (
          <EmptyState title="No expenses" hint="Scanned receipts you confirm will appear here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                  <th className="px-4 py-3 font-medium">Description</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Category</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Date</th>
                  <th className="px-4 py-3 font-medium text-right">Amount</th>
                  <th className="px-4 py-3 font-medium text-center">Claimed</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id} className="border-b border-tb-border dark:border-tb-d-border last:border-0">
                    <td className="px-4 py-3">
                      <p className="font-medium">{e.description || "—"}</p>
                      {e.notes && <p className="text-xs text-tb-muted truncate max-w-[140px] sm:max-w-xs">{e.notes}</p>}
                    </td>
                    <td className="px-4 py-3 text-tb-muted hidden md:table-cell">{e.category_name || "Uncategorised"}</td>
                    <td className="px-4 py-3 text-tb-muted hidden sm:table-cell">{date(e.expense_date)}</td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">{money(e.amount)}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => onToggle(e)} className="align-middle">
                        <Badge tone={e.is_claimed ? "green" : "gray"}>{e.is_claimed ? "Yes" : "No"}</Badge>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setViewing(e)} className="text-xs text-tb-brand hover:underline mr-3">View</button>
                      <button onClick={() => setEditing(e)} className="text-xs text-tb-muted hover:text-tb-ink dark:hover:text-tb-d-text hover:underline mr-3">Edit</button>
                      <button onClick={() => onDelete(e)} className="text-xs text-red-600 hover:underline">Archive</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {viewing && (
        <ViewExpenseModal expense={viewing} onClose={() => setViewing(null)} />
      )}

      {packOpen && <TaxPackModal onClose={() => setPackOpen(false)} />}

      {pracOpen && (
        <PractitionerModal
          practitioners={practitioners}
          releases={releases}
          onClose={() => setPracOpen(false)}
          onChanged={() => { setPracOpen(false); loadPractitioners(); loadReleases(); }}
          onReleaseChanged={loadReleases}
        />
      )}

      {submitOpen && (
        <SubmitToPractitionerModal
          practitioners={practitioners}
          releases={releases}
          onClose={() => setSubmitOpen(false)}
          onReleased={loadReleases}
        />
      )}

      {editing && (
        <EditExpenseModal
          expense={editing}
          categories={cats}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EditExpenseModal({ expense, categories, onClose, onSaved }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    description: expense.description || "",
    amount: expense.amount ?? "",
    expense_date: expense.expense_date ? expense.expense_date.slice(0, 10) : "",
    category_id: expense.category_id || "",
    notes: expense.notes || "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        description: form.description.trim(),
        amount: parseFloat(form.amount),
        notes: form.notes,
      };
      if (form.category_id) body.category_id = form.category_id;
      if (form.expense_date) body.expense_date = form.expense_date;
      await updateExpense(expense.id, body);
      toast.success("Expense updated");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="Edit expense" width="max-w-lg">
      <div className="space-y-4">
        <Field label="Description"><Input value={form.description} onChange={set("description")} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Amount"><Input type="number" step="0.01" min="0" value={form.amount} onChange={set("amount")} /></Field>
          <Field label="Date"><Input type="date" value={form.expense_date} onChange={set("expense_date")} /></Field>
        </div>
        <Field label="Category">
          <Select value={form.category_id} onChange={set("category_id")}>
            <option value="">Uncategorised</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </Field>
        <Field label="Notes"><Input value={form.notes} onChange={set("notes")} placeholder="Optional" /></Field>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}

function ViewExpenseModal({ expense, onClose }) {
  return (
    <Modal open onClose={onClose} title="Expense" width="max-w-3xl" bodyClassName="p-6">
      <div className="space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xl font-bold">{expense.description || "Expense"}</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Badge tone={expense.is_claimed ? "green" : "gray"}>{expense.is_claimed ? "Claimed" : "Not claimed"}</Badge>
              <span className="text-sm text-tb-muted">{expense.category_name || "Uncategorised"}</span>
              <span className="text-sm text-tb-muted">· {date(expense.expense_date)}</span>
            </div>
          </div>
          <p className="text-2xl font-bold text-tb-brand tabular-nums shrink-0">{money(expense.amount)}</p>
        </div>

        {expense.notes && (
          <div className="rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset p-3 text-sm">{expense.notes}</div>
        )}

        <div className="pt-4 border-t border-tb-border dark:border-tb-d-border">
          <p className="text-xs uppercase tracking-wider text-tb-muted mb-3">Source receipt</p>
          {expense.receipt_id
            ? <ReceiptPanel receiptId={expense.receipt_id} highlightItemId={expense.receipt_item_id} highlightAmount={expense.amount} highlightLabel={expense.description} />
            : <p className="text-sm text-tb-muted">This expense isn't linked to a receipt.</p>}
        </div>
      </div>
    </Modal>
  );
}

function TaxPackModal({ onClose }) {
  const toast = useToast();
  const [year, setYear] = useState(TAX_YEARS[0]);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const blob = await downloadTaxPack(year);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TaxPack-TY${year}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Tax pack downloaded");
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Create tax pack" width="max-w-md">
      <div className="space-y-4">
        <p className="text-sm text-tb-muted">
          Builds a zip with a structured Excel sheet, a PDF summary, and all the receipt images
          for your claimed deductions in the selected tax year.
        </p>
        <Field label="Tax year">
          <Select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))}>
            {TAX_YEARS.map((y) => <option key={y} value={y}>TY {y}</option>)}
          </Select>
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={download} disabled={busy}>{busy ? "Preparing…" : "Download"}</Button>
        </div>
      </div>
    </Modal>
  );
}
function PractitionerModal({ practitioners, releases, onClose, onChanged, onReleaseChanged }) {
  const toast = useToast();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleRedeem = async () => {
    setError(""); setSuccess("");
    if (!code.trim()) { setError("Enter a connect code."); return; }
    setBusy(true);
    try {
      await redeemCode(code.trim().toUpperCase());
      setSuccess("Practitioner linked successfully.");
      setTimeout(onChanged, 800);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async (linkId) => {
    if (!window.confirm("Remove this practitioner\u2019s access to your deductions?")) return;
    try {
      await unlinkPractitioner(linkId);
      onChanged();
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleRelease = async (year) => {
    const isReleased = (releases || []).some((r) => r.tax_year === year);
    try {
      if (isReleased) {
        await revokeRelease(year);
        toast.success(`TY ${year} access revoked`);
      } else {
        await releaseTaxPack(year);
        toast.success(`TY ${year} released to practitioner`);
      }
      onReleaseChanged();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const hasPrac = practitioners.length > 0;

  return (
    <Modal open onClose={onClose} title="Tax practitioner">
      <div className="space-y-5">
        {/* linked practitioners */}
        {hasPrac && (
          <div>
            <p className="text-xs font-medium text-tb-muted uppercase tracking-wider mb-2">Linked</p>
            <div className="space-y-2">
              {practitioners.map((p) => (
                <div key={p.link_id} className="flex items-center justify-between rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <p className="text-xs text-tb-muted truncate">{p.email}</p>
                  </div>
                  <Button variant="ghost" onClick={() => handleUnlink(p.link_id)} className="text-xs text-red-500 hover:text-red-600">
                    Unlink
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* tax pack releases */}
        {hasPrac && (
          <div>
            <p className="text-xs font-medium text-tb-muted uppercase tracking-wider mb-2">Tax pack access</p>
            <p className="text-xs text-tb-muted mb-3">
              Release a tax year so your practitioner can download your full tax pack.
            </p>
            <div className="space-y-2">
              {TAX_YEARS.map((y) => {
                const released = (releases || []).some((r) => r.tax_year === y);
                return (
                  <div key={y} className="flex items-center justify-between rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset px-3 py-2.5">
                    <span className="text-sm font-medium">Tax year {y}</span>
                    <button
                      onClick={() => toggleRelease(y)}
                      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                        released ? "bg-tb-brand" : "bg-tb-border dark:bg-tb-d-border"
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${
                        released ? "translate-x-[22px] ml-0" : "translate-x-0.5"
                      }`} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* redeem code */}
        <div>
          {hasPrac && <p className="text-xs font-medium text-tb-muted uppercase tracking-wider mb-2">Link another</p>}
          <Field label="Connect code" hint="Your tax practitioner will give you this code.">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. A3BX9K2M"
              className="uppercase tracking-widest"
              onKeyDown={(e) => e.key === "Enter" && handleRedeem()}
            />
          </Field>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {success && <p className="text-sm text-tb-brand">{success}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Close</Button>
          <Button onClick={handleRedeem} disabled={busy || !code.trim()}>Link</Button>
        </div>
      </div>
    </Modal>
  );
}
function SubmitToPractitionerModal({ practitioners, releases, onClose, onReleased }) {
  const toast = useToast();
  const [year, setYear] = useState(TAX_YEARS[0]);
  const [busy, setBusy] = useState(false);

  const isReleased = (releases || []).some((r) => r.tax_year === year);
  const pracNames = practitioners.map((p) => p.name).join(", ");

  const handleSubmit = async () => {
    setBusy(true);
    try {
      await releaseTaxPack(year);
      toast.success(`TY ${year} tax pack submitted to ${pracNames}`);
      onReleased();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    setBusy(true);
    try {
      await revokeRelease(year);
      toast.success(`TY ${year} access revoked`);
      onReleased();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Submit tax pack to practitioner" width="max-w-md">
      <div className="space-y-4">
        <p className="text-sm text-tb-muted">
          This gives <strong>{pracNames}</strong> access to download your full tax pack
          (deductions spreadsheet, PDF summary, and receipt images) for the selected tax year.
        </p>

        <Field label="Tax year">
          <Select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))}>
            {TAX_YEARS.map((y) => <option key={y} value={y}>TY {y}</option>)}
          </Select>
        </Field>

        {isReleased && (
          <div className="flex items-center justify-between rounded-xl bg-tb-brand/10 px-3 py-2.5">
            <div>
              <p className="text-sm font-medium text-tb-brand">Already submitted</p>
              <p className="text-xs text-tb-muted">Your practitioner can download this tax pack.</p>
            </div>
            <Button variant="ghost" onClick={handleRevoke} disabled={busy} className="text-xs text-red-500 hover:text-red-600">
              Revoke
            </Button>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          {!isReleased && (
            <Button onClick={handleSubmit} disabled={busy}>
              {busy ? "Submitting\u2026" : "Submit"}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}